import { useState, useEffect } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { SUPPORTED_LEAGUES } from "../lib/constants";

/**
 * Load rosters from Supabase for all supported leagues.
 * @returns {{ rosters: Object, rostersLoaded: boolean }}
 *
 * READ BEFORE ADDING 'nfl' TO SUPPORTED_LEAGUES (#1037).
 *
 * `rosterMap` is keyed by `team_abbr` ALONE, with no league dimension. That is
 * safe only because NHL and MLB share almost no abbreviations. NFL shares about
 * fifteen with them -- PIT PHI MIN DET SEA TB WSH ARI KC BAL CLE CIN HOU MIA SF
 * -- so the moment 'nfl' joins SUPPORTED_LEAGUES this map silently merges NFL
 * players into NHL and MLB team rosters, with no error anywhere.
 *
 * Deliberately NOT restructured here: as of 2026-09-08 the returned map is
 * never read. It is passed App.js -> AddSlipSheet -> SlipImporter, where it
 * appears only in the props signature. Adding a league dimension to something
 * nothing consumes would be speculative; the honest fix at the point of use is
 * either to key it `[league][team_abbr]` or to delete the prop chain (which
 * touches App.js, a branch_discipline high-risk file, so it needs its own PR).
 */
export function useRosters() {
  const [rosters, setRosters] = useState({});
  const [rostersLoaded, setRostersLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      if (supabase) {
        const { data: rosterRows, error: rosterErr } = await supabase
          .from('rosters')
          .select('team_abbr, player_name')
          .eq('active', true)
          .in('league', SUPPORTED_LEAGUES);

        if (!rosterErr && rosterRows?.length) {
          const rosterMap = {};
          rosterRows.forEach(({ team_abbr, player_name }) => {
            if (!rosterMap[team_abbr]) rosterMap[team_abbr] = [];
            rosterMap[team_abbr].push(player_name);
          });
          setRosters(rosterMap);
          setRostersLoaded(true);
          return;
        }
      }
      Sentry.addBreadcrumb({
        category: 'useRosters',
        message: 'Supabase roster fetch failed, fallback unavailable',
        level: 'warning',
      });
      setRosters({});
      setRostersLoaded(true);
    })();
  }, []);

  return { rosters, rostersLoaded };
}
